import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../utils/translations';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import ChangePassword from '../components/ChangePassword';
import SubscriptionManagement from '../components/SubscriptionManagement';
import NotificationSettings from '../components/NotificationSettings';
import ActivityCalendar from '../components/ActivityCalendar';
import { useSubscription } from '../contexts/SubscriptionContext';
import { deckService } from '../services/deckService';
import quizService from '../services/quizService';
import matchService from '../services/matchService';
import { formatMonthlyStudyTime } from '../utils/timeUtils';

type TabType = 'account' | 'subscription' | 'notifications' | 'security';

const Profile: React.FC = () => {
  const { user, isEmailVerified, refreshAuth } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('account');
  const { subscription } = useSubscription();
  const [deckStats, setDeckStats] = useState<{ totalDecks: number; totalCards: number } | null>(null);
  const [quizStats, setQuizStats] = useState<{ totalQuizzes: number; totalAttempts: number; averageScore: number; totalQuestions: number } | null>(null);
  const [matchStats, setMatchStats] = useState<{ totalGames: number; totalAttempts: number; totalPairs?: number } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Refresh user data when the page loads to ensure streak and other stats are up-to-date
  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  // Fetch usage stats
  useEffect(() => {
    const fetchDeckStats = async () => {
      try {
        const stats = await deckService.getDeckStats();
        setDeckStats(stats);
      } catch (error) {
        console.error('Error fetching deck stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    const fetchQuizStats = async () => {
      try {
        const [stats, quizzes] = await Promise.all([
          quizService.getQuizStats(),
          quizService.getQuizzes()
        ]);
        
        const totalQuestions = quizzes.reduce((sum, quiz) => sum + quiz.questionsCount, 0);
        
        setQuizStats({
          totalQuizzes: stats.totalQuizzes,
          totalAttempts: stats.totalAttempts,
          averageScore: stats.averageScore,
          totalQuestions
        });
      } catch (error) {
        console.error('Error fetching quiz stats:', error);
      }
    };

    const fetchMatchStats = async () => {
      try {
        const stats = await matchService.getMatchStats();
        setMatchStats({
          totalGames: stats.totalGames,
          totalAttempts: stats.totalAttempts
        });
      } catch (error) {
        console.error('Error fetching match stats:', error);
      }
    };

    fetchDeckStats();
    fetchQuizStats();
    fetchMatchStats();
  }, []);

  const tabs = [
    { 
      id: 'account' as TabType, 
      name: t('profile.accountTab'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    { 
      id: 'subscription' as TabType, 
      name: t('profile.subscriptionTab'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      )
    },
    { 
      id: 'notifications' as TabType, 
      name: t('profile.notificationsTab'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      )
    },
    { 
      id: 'security' as TabType, 
      name: t('profile.securityTab'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    },
  ];

  return (
    <div className="max-w-6xl mx-auto min-h-screen transition-colors duration-300 px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6">
      <div className="space-y-3 sm:space-y-4 md:space-y-6">
        {/* Header */}
        <div className="px-1">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{t('profile.profile')}</h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-700 dark:text-gray-300">{t('profile.manageAccount')}</p>
        </div>

        <EmailVerificationBanner />

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            {/* Mobile: Enhanced Dropdown */}
            <div className="sm:hidden relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </div>
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value as TabType)}
                className="block w-full py-3 pl-10 pr-10 text-sm font-medium border-0 focus:ring-2 focus:ring-purple-500 focus:ring-inset bg-white dark:bg-gray-800 text-gray-900 dark:text-white appearance-none cursor-pointer"
                style={{ backgroundImage: 'none' }}
              >
                {tabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {tab.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            
            {/* Desktop & Tablet: Tab Buttons */}
            <nav className="hidden sm:flex -mb-px overflow-x-auto" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group inline-flex items-center py-4 px-4 lg:px-6 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200 flex-shrink-0
                    ${activeTab === tab.id
                      ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <span className={`mr-2 ${activeTab === tab.id ? 'text-purple-500 dark:text-purple-400' : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'}`}>
                    {tab.icon}
                  </span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-3 sm:p-4 md:p-6">
            {/* Account Tab */}
            {activeTab === 'account' && (
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <h3 className="text-base sm:text-lg leading-6 font-medium text-gray-900 dark:text-white mb-3 sm:mb-4">
                    {t('profile.accountInformation')}
                  </h3>
                  
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-2">
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">{t('profile.name')}</dt>
                      <dd className="mt-1 text-sm sm:text-base text-gray-900 dark:text-white break-words">{user?.name}</dd>
                    </div>
                    
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">{t('profile.email')}</dt>
                      <dd className="mt-1 text-sm sm:text-base text-gray-900 dark:text-white flex flex-wrap items-center gap-1">
                        <span className="break-all">{user?.email}</span>
                        {isEmailVerified ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 whitespace-nowrap">
                            {t('subscription.verified')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 whitespace-nowrap">
                            {t('subscription.unverified')}
                          </span>
                        )}
                      </dd>
                    </div>
                    
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">{t('profile.memberSince')}</dt>
                      <dd className="mt-1 text-sm sm:text-base text-gray-900 dark:text-white">
                        {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('pt-BR') : 'N/A'}
                      </dd>
                    </div>
                    
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">{t('profile.lastLogin')}</dt>
                      <dd className="mt-1 text-sm sm:text-base text-gray-900 dark:text-white">
                        {user?.lastLogin ? new Date(user.lastLogin).toLocaleDateString('pt-BR') : 'N/A'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            )}

            {/* Subscription Tab */}
            {activeTab === 'subscription' && (
              <div className="space-y-4 sm:space-y-6">
                <SubscriptionManagement />
                
                {/* Usage Stats Section */}
                {subscription && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 sm:pt-6">
                    <h3 className="text-base sm:text-lg leading-6 font-medium text-gray-900 dark:text-white mb-3 sm:mb-4">
                      Seu Uso Atual
                    </h3>
                    
                    {subscription.subscriptionTier === 'free' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Decks</h4>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              (deckStats?.totalDecks || 0) >= 3 
                                ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' 
                                : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            }`}>
                              {(deckStats?.totalDecks || 0) >= 3 ? 'Limite Atingido' : 'Disponível'}
                            </span>
                          </div>
                          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                            {statsLoading ? '...' : deckStats?.totalDecks || 0}
                            <span className="text-lg text-gray-500 dark:text-gray-400"> / 3</span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {Math.max(0, 3 - (deckStats?.totalDecks || 0))} decks restantes
                          </p>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Cartões</h4>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              (deckStats?.totalCards || 0) >= 300 
                                ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                                : (deckStats?.totalCards || 0) >= 240 
                                ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                                : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            }`}>
                              {(deckStats?.totalCards || 0) >= 300 ? 'Limite Atingido' : 
                               (deckStats?.totalCards || 0) >= 240 ? 'Perto do Limite' : 'Disponível'}
                            </span>
                          </div>
                          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                            {statsLoading ? '...' : deckStats?.totalCards || 0}
                            <span className="text-lg text-gray-500 dark:text-gray-400"> / 300</span>
                          </div>
                          <div className="mb-2">
                            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  (deckStats?.totalCards || 0) >= 300 
                                    ? 'bg-red-600' 
                                    : (deckStats?.totalCards || 0) >= 240 
                                    ? 'bg-yellow-600' 
                                    : 'bg-green-600'
                                }`}
                                style={{ width: `${Math.min(((deckStats?.totalCards || 0) / 300) * 100, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {Math.max(0, 300 - (deckStats?.totalCards || 0))} flashcards restantes
                          </p>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Quizzes</h4>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              (quizStats?.totalQuizzes || 0) >= 3 
                                ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' 
                                : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            }`}>
                              {(quizStats?.totalQuizzes || 0) >= 3 ? 'Limite Atingido' : 'Disponível'}
                            </span>
                          </div>
                          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                            {statsLoading ? '...' : quizStats?.totalQuizzes || 0}
                            <span className="text-lg text-gray-500 dark:text-gray-400"> / 3</span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {Math.max(0, 3 - (quizStats?.totalQuizzes || 0))} quizzes restantes
                          </p>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Quizzes questões</h4>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              (quizStats?.totalQuestions || 0) >= 45 
                                ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                                : (quizStats?.totalQuestions || 0) >= 36 
                                ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                                : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            }`}>
                              {(quizStats?.totalQuestions || 0) >= 45 ? 'Limite Atingido' : 
                               (quizStats?.totalQuestions || 0) >= 36 ? 'Perto do Limite' : 'Disponível'}
                            </span>
                          </div>
                          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                            {statsLoading ? '...' : quizStats?.totalQuestions || 0}
                            <span className="text-lg text-gray-500 dark:text-gray-400"> / 45</span>
                          </div>
                          <div className="mb-2">
                            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  (quizStats?.totalQuestions || 0) >= 45 
                                    ? 'bg-red-600' 
                                    : (quizStats?.totalQuestions || 0) >= 36 
                                    ? 'bg-yellow-600' 
                                    : 'bg-green-600'
                                }`}
                                style={{ width: `${Math.min(((quizStats?.totalQuestions || 0) / 45) * 100, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {Math.max(0, 45 - (quizStats?.totalQuestions || 0))} questões restantes
                          </p>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Matches</h4>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              (matchStats?.totalGames || 0) >= 3 
                                ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' 
                                : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            }`}>
                              {(matchStats?.totalGames || 0) >= 3 ? 'Limite Atingido' : 'Disponível'}
                            </span>
                          </div>
                          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                            {statsLoading ? '...' : matchStats?.totalGames || 0}
                            <span className="text-lg text-gray-500 dark:text-gray-400"> / 3</span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {Math.max(0, 3 - (matchStats?.totalGames || 0))} matches restantes
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">∞</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Decks Ilimitados</p>
                          {deckStats && deckStats.totalDecks > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {deckStats.totalDecks} criados
                            </p>
                          )}
                        </div>
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">∞</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Decks Cards Ilimitados</p>
                          {deckStats && deckStats.totalCards > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {deckStats.totalCards} criados
                            </p>
                          )}
                        </div>
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">∞</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Quizzes Ilimitados</p>
                          {quizStats && quizStats.totalQuizzes > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {quizStats.totalQuizzes} criados
                            </p>
                          )}
                        </div>
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">∞</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Quizzes questions Ilimitados</p>
                          {quizStats && quizStats.totalQuestions > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {quizStats.totalQuestions} criados
                            </p>
                          )}
                        </div>
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">∞</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Matches Ilimitados</p>
                          {matchStats && matchStats.totalGames > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {matchStats.totalGames} criados
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div>
                <NotificationSettings />
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div>
                <ChangePassword />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
